import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import { graphDataService, GraphData, GraphNode, GraphLink } from '../services/graphDataService';
import { GraphControls, GraphFilters, GraphForces, GraphDisplay } from './GraphControls';

interface GraphViewProps {
  onNodeClick?: (nodeId: string, nodeData: GraphNode) => void;
  focusNodeId?: string;
}

const Container = styled.div`
  width: 100%;
  height: 100vh;
  background: var(--color-background);
  position: relative;
  overflow: hidden;
`;

const Tooltip = styled.div<{ x: number; y: number; visible: boolean }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 12px;
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.2s;
  transform: translate(-50%, -120%);
  max-width: 300px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--color-text);
`;

const TooltipContent = styled.div`
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-secondary);
`;

// Color schemes for different node types
const getNodeColor = (node: GraphNode): string => {
  if (node.type === 'query') {
    return 'var(--color-primary)';
  }
  
  // Color concepts by type
  const conceptColors: Record<string, string> = {
    THEORY: '#9333EA',
    CANON: '#3B82F6',
    SHIP: '#EC4899',
    CONTROVERSY: '#DC2626',
    MEME: '#10B981',
    CHARACTER: '#F59E0B',
    EVIDENCE: '#6366F1',
    DEBATE: '#64748B',
    GENERAL: '#6B7280'
  };
  
  return conceptColors[node.metadata.conceptType || 'GENERAL'] || '#6B7280';
};

export const GraphView: React.FC<GraphViewProps> = ({ onNodeClick, focusNodeId }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<GraphNode, GraphLink> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ x: 0, y: 0, visible: false, node: null as GraphNode | null });
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [filteredData, setFilteredData] = useState<GraphData>({ nodes: [], links: [] });
  
  // Control states
  const [filters, setFilters] = useState<GraphFilters>({
    searchQuery: '',
    showOrphans: true,
    conceptTypes: [],
    minFrequency: 1
  });
  
  const [forces, setForces] = useState<GraphForces>({
    centerForce: 0.5,
    repelForce: -300,
    linkForce: 1,
    linkDistance: 150
  });
  
  const [display, setDisplay] = useState<GraphDisplay>({
    showArrows: false,
    textFade: 2,
    nodeSize: 1,
    linkThickness: 1
  });
  
  useEffect(() => {
    loadGraphData();
  }, []);
  
  const loadGraphData = async () => {
    setLoading(true);
    try {
      const data = await graphDataService.buildGraphData();
      setGraphData(data);
      setFilteredData(data);
    } catch (error) {
      console.error('Error loading graph data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Apply filters whenever they change
  useEffect(() => {
    if (graphData.nodes.length === 0) return;
    
    const filtered = graphDataService.filterGraph(graphData, {
      searchQuery: filters.searchQuery,
      showOrphans: filters.showOrphans,
      conceptTypes: filters.conceptTypes.length > 0 ? filters.conceptTypes : undefined,
      minFrequency: filters.minFrequency
    });
    
    setFilteredData(filtered);
  }, [filters, graphData]);
  
  // Update forces when they change
  useEffect(() => {
    if (!simulationRef.current) return;
    
    const simulation = simulationRef.current;
    
    // Update center force
    simulation.force('center', d3.forceCenter(
      containerRef.current!.clientWidth / 2, 
      containerRef.current!.clientHeight / 2
    ).strength(forces.centerForce));
    
    // Update charge force
    simulation.force('charge', d3.forceManyBody()
      .strength(forces.repelForce)
      .distanceMax(300));
    
    // Update link force
    const linkForce = simulation.force('link') as d3.ForceLink<GraphNode, GraphLink>;
    if (linkForce) {
      linkForce
        .strength(d => d.strength * forces.linkForce)
        .distance(forces.linkDistance);
    }
    
    // Reheat the simulation
    simulation.alpha(0.3).restart();
  }, [forces]);
  
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || loading || filteredData.nodes.length === 0) return;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove();
    
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);
    
    // Create zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    
    svg.call(zoom);
    
    // Create main group for zoom/pan
    const g = svg.append('g');
    
    // Create force simulation
    const simulation = d3.forceSimulation<GraphNode>(filteredData.nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(filteredData.links)
        .id(d => d.id)
        .strength(d => d.strength * forces.linkForce)
        .distance(forces.linkDistance))
      .force('charge', d3.forceManyBody()
        .strength(forces.repelForce)
        .distanceMax(300))
      .force('center', d3.forceCenter(width / 2, height / 2).strength(forces.centerForce))
      .force('collision', d3.forceCollide<GraphNode>()
        .radius(d => getNodeRadius(d) + 30));
    
    // Store simulation reference
    simulationRef.current = simulation;
    
    // Create links
    const link = g.append('g')
      .selectAll('line')
      .data(filteredData.links)
      .join('line')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.3)
      .attr('stroke-width', d => Math.sqrt(d.strength) * 2 * display.linkThickness);
    
    // Create node groups
    const node = g.append('g')
      .selectAll('g')
      .data(filteredData.nodes)
      .join('g')
      .call(d3.drag<SVGGElement, GraphNode, GraphNode>()
        .on('start', dragStarted)
        .on('drag', dragged)
        .on('end', dragEnded) as any);
    
    // Add circles to nodes
    node.append('circle')
      .attr('r', d => getNodeRadius(d))
      .attr('fill', d => getNodeColor(d))
      .attr('fill-opacity', 0.8)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .on('mouseover', handleMouseOver)
      .on('mouseout', handleMouseOut)
      .on('click', handleNodeClick);
    
    // Add labels to nodes
    node.append('text')
      .text(d => d.label)
      .attr('x', 0)
      .attr('y', d => d.type === 'query' ? 25 : 20)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'hanging')
      .style('fill', 'var(--color-text)')
      .style('font-size', d => d.type === 'query' ? '10px' : '8px')
      .style('font-weight', d => d.type === 'query' ? '600' : '400')
      .style('pointer-events', 'none')
      .style('user-select', 'none');
    
    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => (d.source as any).x!)
        .attr('y1', d => (d.source as any).y!)
        .attr('x2', d => (d.target as any).x!)
        .attr('y2', d => (d.target as any).y!);
      
      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });
    
    // Focus on specific node if provided
    if (focusNodeId) {
      const focusNode = filteredData.nodes.find(n => n.id === focusNodeId);
      if (focusNode) {
        // Wait for initial simulation to settle
        setTimeout(() => {
          const scale = 1.5;
          const transform = d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(scale)
            .translate(-focusNode.x!, -focusNode.y!);
          
          svg.transition()
            .duration(750)
            .call(zoom.transform, transform);
        }, 1000);
      }
    }
    
    // Drag functions
    function dragStarted(event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }
    
    function dragged(event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }
    
    function dragEnded(event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>) {
      if (!event.active) simulation.alphaTarget(0);
      // Keep node fixed after dragging (remove these lines to unfix)
      // event.subject.fx = null;
      // event.subject.fy = null;
    }
    
    function handleMouseOver(event: MouseEvent, d: GraphNode) {
      const [x, y] = d3.pointer(event, container);
      setTooltip({ x, y, visible: true, node: d });
    }
    
    function handleMouseOut() {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
    
    function handleNodeClick(event: MouseEvent, d: GraphNode) {
      event.stopPropagation();
      if (onNodeClick) {
        onNodeClick(d.id, d);
      }
    }
    
    function getNodeRadius(node: GraphNode): number {
      const baseRadius = node.type === 'query' ? 15 : 10;
      return (baseRadius + Math.log(node.frequency + 1) * 3) * display.nodeSize;
    }
    
    // Cleanup
    return () => {
      simulation.stop();
      simulationRef.current = null;
    };
  }, [filteredData, loading, focusNodeId, onNodeClick, forces, display]);
  
  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading knowledge graph...</LoadingContainer>
      </Container>
    );
  }
  
  return (
    <Container ref={containerRef}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      <GraphControls
        filters={filters}
        forces={forces}
        display={display}
        onFiltersChange={setFilters}
        onForcesChange={setForces}
        onDisplayChange={setDisplay}
      />
      {tooltip.node && (
        <Tooltip x={tooltip.x} y={tooltip.y} visible={tooltip.visible}>
          <TooltipTitle>
            {tooltip.node.label}
          </TooltipTitle>
          <TooltipContent>
            {tooltip.node.type === 'query' ? (
              <>
                <div>Intent: {tooltip.node.metadata.intent}</div>
                <div>Explored {tooltip.node.frequency} time{tooltip.node.frequency > 1 ? 's' : ''}</div>
                {tooltip.node.metadata.overview && (
                  <div style={{ marginTop: 8 }}>{tooltip.node.metadata.overview}</div>
                )}
              </>
            ) : (
              <>
                <div>Type: {tooltip.node.metadata.conceptType}</div>
                <div>Frequency: {tooltip.node.frequency}</div>
              </>
            )}
          </TooltipContent>
        </Tooltip>
      )}
    </Container>
  );
};